<template>
  <div class="uk-container">
    <img
      class="logo"
      src="../../common/assets/logo.png"
    />

    <h1 class="mb-5">Promotion Request System</h1>

    <div v-if="error" class="uk-alert-danger" uk-alert>
      <textarea v-model="error" rows="30" cols="60"></textarea>
    </div>

    <template v-if="initializing">
      <div class="uk-margin" uk-spinner="ratio: 3"></div>
    </template>

    <template v-else-if="done">
      <p>Thank you for submitting your information. We'll get in touch with you as soon as possible.</p>
    </template>

    <template v-else>
      <p>
        Fields marked with an <label><span class="asterisk">*</span></label> are required.
      </p>
      <br>

      <ui-form
        ref="form"
        :model="model"
        :rules="validationRules"
      >
        <ui-form-partial>
          <div class="uk-child-width-1-3@m uk-margin" uk-grid>
            <div>
              <ui-form-item
                label="Project Point of Contact (POC)"
                prop="poc.name"
                required>
                <ui-form-input v-model="model.poc.name" name="poc[name]" />
              </ui-form-item>
            </div>
            <div>
              <ui-form-item
                label="POC Phone"
                prop="poc.phone"
                required>
                <ui-form-input v-model="model.poc.phone" name="poc[phone]" />
              </ui-form-item>
            </div>
            <div>
              <ui-form-item
                label="POC Email"
                prop="poc.email"
                required
                email>
                <ui-form-input type="email" v-model="model.poc.email" name="poc[email]" />
              </ui-form-item>
            </div>
          </div>

          <hr class="uk-margin-small uk-visible@m">

          <div class="uk-margin uk-grid-divider uk-grid-medium" uk-grid>
            <div class="uk-width-1-1@s uk-width-1-3@m">
              <ui-form-item
                label="For which campus(es) does your project apply?"
                prop="campus"
                required-choice>
                <ui-form-checkbox
                  v-for="option in campusOptions"
                  :key="option.id"
                  :value="option.id"
                  v-model="model.campus"
                  :label="option.name"
                  name="campus[]"
                />
              </ui-form-item>
            </div>
            <div class="uk-width-1-1@s uk-width-expand@m">
              <ui-form-item
                label="Requesting Ministry or Department"
                prop="department">
                <ui-form-input v-model="model.department" name="department" />
              </ui-form-item>

              <div class="uk-child-width-1-2@m uk-margin" uk-grid>
                <div>
                  <ui-form-item
                    label="The campus pastor and/or department head has approved this project."
                    prop="pastorApproved"
                    required-true>
                    <ui-form-checkbox v-model="model.pastorApproved" name="pastorApproved"
                      :value="true" label="Yes" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item
                    label="Pastor's and/or department head's name"
                    prop="pastorName"
                    required>
                    <ui-form-input v-model="model.pastorName" name="pastorName" />
                  </ui-form-item>
                </div>
              </div>
            </div>
          </div>

          <hr class="uk-margin-small uk-visible@m">

          <div class="uk-margin uk-grid-divider uk-grid-collapse uk-grid-medium" uk-grid>
            <div class="uk-width-1-1@s uk-width-1-3@m">
              <ui-form-item
                label="This has been created as an event in Church Community Builder (CCB)."
                prop="ccb.created">
                <ui-form-checkbox v-model="model.ccb.created" name="ccb[created]" :value="true" label="Yes" />
              </ui-form-item>
            </div>
            <div class="uk-width-1-1@s uk-width-expand@m">
              <div class="uk-child-width-expand@m" uk-grid>
                <div>
                  <ui-form-item
                    label="Event title in CCB"
                    prop="ccb.title"
                  >
                    <ui-form-input v-model="model.ccb.title" name="ccb[title]" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item
                    label="Link to CCB form (if created)"
                    prop="ccb.formLink"
                  >
                    <ui-form-input v-model="model.ccb.formLink" name="ccb[formLink]" />
                  </ui-form-item>
                </div>
              </div>
            </div>
          </div>
        </ui-form-partial>

        <ui-form-partial label="Text Blasts">
          <ui-form-item
            label="Do you need text blasts to promote this event?"
            prop="textBlasts.required"
            required-radio>
            <ui-form-radio v-model="model.textBlasts.required" :value="true" label="Yes" name="textBlasts[required]" />
            <ui-form-radio v-model="model.textBlasts.required" :value="false" label="No" name="textBlasts[required]" />
          </ui-form-item>

          <template v-if="model.textBlasts.required">
            <ui-form-item
              label="What would you like to communicate?"
              prop="textBlasts.message">
              <ui-form-input v-model="model.textBlasts.message" name="textBlasts[message]" type="textarea" rows="5" />
            </ui-form-item>

            <div class="uk-alert-danger" uk-alert>
              <b>NOTE:</b> Text blasts are 130 characters max.
            </div>
          </template>
        </ui-form-partial>

        <ui-form-partial label="Project Details">
          <ui-form-item
            label="Title"
            prop="event.title"
            required>
            <ui-form-input v-model="model.event.title" name="event[title]" />
          </ui-form-item>

          <ui-form-item
            label="Type"
            prop="event.type"
            required-radio>
            <ui-form-radio v-model="model.event.type" value="Event" label="Event" name="event[type]" />
            <ui-form-radio v-model="model.event.type" value="Campaign" label="Campaign (No Event)" name="event[type]" />
            <template #help>
              An <b>event</b> is a planned occasion, which includes an in-person or virtual activity, during a
              defined period of time (e.g. a course, a musical, a wellness fair, a community service project, a
              town hall summit). A <b>campaign</b> is  an organized course of action to promote a call to action
              (e.g. subscribe, volunteer, give, watch, etc.) to an audience (e.g. a fundraising campaign, a volunteer
              recruitment effort, a community service pledge, an email subscription campaign).
            </template>
          </ui-form-item>

          <ui-form-item label="Date of the event/campaign (start date)">
            <ui-form-repeater
              v-model="model.event.dates"
              :new-item-fn="newEventDate"
              :max-items="2"
              inline-fields
            >
              <template #item="{ item }">
                <ui-form-date v-model="item" name="event[dates][]" />
              </template>
              <template #add-item-text>Add End Date (if applicable)</template>
            </ui-form-repeater>
          </ui-form-item>

          <ui-form-item
            label="If there is a registration period associated with the event/campaign, please enter those dates below."
            prop="event.registrationPeriod"
          >
            <ui-form-input v-model="model.event.registrationPeriod" name="event[registrationPeriod]" type="textarea" rows="4" />
          </ui-form-item>

          <ui-form-item
            label="Location of the event/campaign"
            prop="event.location"
            required>
            <ui-form-input v-model="model.event.location" name="event[location]" />
          </ui-form-item>

          <ui-form-item
            label="Is this activity being cross-promoted in conjunction with another event, ministry, community partner or sponsor?"
            prop="event.crossPromoted"
            required-radio>
            <ui-form-radio v-model="model.event.crossPromoted" :value="true" label="Yes" name="event[crossPromoted]" />
            <ui-form-radio v-model="model.event.crossPromoted" :value="false" label="No" name="event[crossPromoted]" />
          </ui-form-item>

          <ui-form-item
            v-show="model.event.crossPromoted"
            label="Please provide partner logo artwork">
            <ui-form-uppy
              v-model="model.event.partnerArtwork"
              :params-url="uploadParamsUrl"
              allow-multiple
              form-field-name="event[crossPromoted]"
            />
          </ui-form-item>

          <ui-form-item
            label="Event/campaign details"
            prop="event.details">
            <ui-form-input v-model="model.event.details" name="event[details]" type="textarea" rows="10" />
            <template #help>
              Provide a description of the event/campaign that includes event info (e.g. pre-registration required,
              virtual event only, ticket cost), event highlights (e.g. live music, on-site childcare, free to attend,
              prizes and giveaways), target audience (i.e. women, parents of teens, volunteer leaders, etc.), and
              what the desired action we want the audience member to take (e.g. register, donate, tune in, shop, etc.)
            </template>
          </ui-form-item>
        </ui-form-partial>

        <ui-form-partial>
          <template slot="label">
            Graphics
            <span class="warning-text">Graphics require a 14 business day lead time</span>
          </template>

          <ui-form-item
            label="Does this event/campaign require graphic design support?"
            prop="graphics.required"
            required-radio>
            <ui-form-radio v-model="model.graphics.required" :value="true" label="Yes" name="graphics[required]" />
            <ui-form-radio v-model="model.graphics.required" :value="false" label="No" name="graphics[required]" />
          </ui-form-item>

          <ui-form-item
            v-if="model.graphics.required"
            label="Select the formats you require."
            prop="graphics.formats"
            required>
            <ui-form-checkbox
              v-for="format in graphicsFormatOptions"
              :key="format"
              :value="format"
              v-model="model.graphics.formats"
              :label="format"
              name="graphics[formats][]"
            />
            <template #help>
              Note: ONLY select the exact formats you require, DO NOT select all by default.
            </template>
          </ui-form-item>

          <ui-form-item
            v-if="model.graphics.required"
            label="Special Format size"
            prop="graphics.specialFormatSize">
            <ui-form-input v-model="model.graphics.specialFormatSize" name="graphics[specialFormatSize]" />
          </ui-form-item>

          <ui-form-item
            v-if="model.graphics.required"
            label="Provide any info/text, date, and link that you would like to have added to the graphic. This should not exceed 20 words."
            prop="graphics.detailsToInclude"
            required>
            <ui-form-input v-model="model.graphics.detailsToInclude" name="graphics[detailsToInclude]" type="textarea" rows="5" />
          </ui-form-item>

          <ui-form-item
            v-if="model.graphics.required"
            label="Provide any design guidelines, color palette, theme. Share links to third party sources if needed."
            prop="graphics.designGuidelines">
            <ui-form-input v-model="model.graphics.designGuidelines" name="graphics[designGuidelines]" type="textarea" rows="5" />
          </ui-form-item>

          <ui-form-item
            v-show="model.graphics.required"
            label="Upload any files that we should use as inspiration."
            prop="graphics.inspirationFiles">
            <ui-form-uppy
              v-model="model.graphics.inspirationFiles"
              :params-url="uploadParamsUrl"
              allow-multiple
              form-field-name="graphics[inspirationFiles]"
            />
          </ui-form-item>
        </ui-form-partial>

        <ui-form-partial label="Digital Arts">
          <ui-form-item
            label="Do you need additional design support?"
            prop="designSupport.required"
            required-radio>
            <ui-form-radio v-model="model.designSupport.required" :value="true" label="Yes" name="designSupport[required]" />
            <ui-form-radio v-model="model.designSupport.required" :value="false" label="No" name="designSupport[required]" />
          </ui-form-item>

          <template v-if="model.designSupport.required">
            <ui-form-item
              label="Share details about the additional design support you are requesting."
              prop="designSupport.services"
              required>
              <ui-form-checkbox
                v-for="option in designSupportOptions"
                :key="option"
                :value="option"
                v-model="model.designSupport.services"
                :label="option"
                name="designSupport[services][]"
              />
              <template #help>
                A three (3) to four (4) week lead time is requested for printed items and materials.
              </template>
            </ui-form-item>

            <ui-form-item
              label="Provide additional information for your request, such as flag sizes, quantity of pens, any specific print size requirements, etc."
              prop="graphics.additionalInformation">
              <ui-form-input v-model="model.graphics.additionalInformation" name="graphics[additionalInformation]" type="textarea" rows="5" />
            </ui-form-item>
          </template>
        </ui-form-partial>

        <ui-form-partial label="Registration">
          <ui-form-item
            label="Does this event/campaign require a registration solution (i.e. CCB, Eventbrite, PCO, etc.)?"
            prop="registration.required"
            required-radio>
            <ui-form-radio v-model="model.registration.required" :value="true" label="Yes" name="registration[required]" />
            <ui-form-radio v-model="model.registration.required" :value="false" label="No" name="registration[required]" />
            <template #help>
              If YES, please work with IT to setup.
            </template>
          </ui-form-item>

          <template v-if="model.registration.required">
            <ui-form-item label="When will registration and/or ticket sales open?">
              <ui-form-date v-model="model.registration.ticketSalesOpen" name="registration[ticketSalesOpen]" />
            </ui-form-item>

            <ui-form-item label="When will registration and/or ticket sales close?">
              <ui-form-date v-model="model.registration.ticketSalesClose" name="registration[ticketSalesClose]" />
            </ui-form-item>

            <ui-form-item label="Registration solution requirements">
              <ui-form-input v-model="model.registration.requirements" name="registration[requirements]" type="textarea" rows="5" />
              <template #help>
                (list details like ticket types needed, discount or promo codes needed for volunteers, add-on question / survey likes t-shirt size or campus attendance)
              </template>
            </ui-form-item>
          </template>
        </ui-form-partial>

        <ui-form-partial label="Video Production">
          <div class="uk-alert-primary" uk-alert>
            <b>NOTE:</b> There is a 4-6 week lead time required for scheduling and editing.
            No video production is approved until confirmation is received from that department.
          </div>

          <ui-form-item
            label="Will this event require video production support?"
            prop="video.required"
            required-radio>
            <ui-form-radio v-model="model.video.required" :value="true" label="Yes" name="video[required]" />
            <ui-form-radio v-model="model.video.required" :value="false" label="No" name="video[required]" />
          </ui-form-item>

          <template v-if="model.video.required">
            <ui-form-item
              label="Check all that apply"
              prop="video.services"
              required-choice>
              <ui-form-checkbox
                v-for="option in videoServiceOptions"
                :key="option.id"
                :value="option.id"
                v-model="model.video.services"
                :label="option.name"
                name="video[services][]"
              />
            </ui-form-item>
          </template>
        </ui-form-partial>

        <ui-form-partial label="Finance">
          <ui-form-item
            label="Does this event or campaign include any monetary transactions?"
            prop="finance.monetaryTransactions"
            required-radio>
            <ui-form-radio v-model="model.finance.monetaryTransactions" :value="true" label="Yes" name="finance[monetaryTransactions]" />
            <ui-form-radio v-model="model.finance.monetaryTransactions" :value="false" label="No" name="finance[monetaryTransactions]" />
          </ui-form-item>

          <template v-if="model.finance.monetaryTransactions">
            <div class="uk-alert-primary" uk-alert>
              Approval is required from the Finance Department to proceed.
            </div>

            <div class="uk-alert-primary" uk-alert>
              Requests for monetary transactions may only be sent by a Zion Church staff member.
            </div>

            <br>
            <div class="uk-text-lead uk-heading-bullet">Cash or Checks</div>

            <ui-form-item
              label="Will you need to collect cash or checks?"
              prop="finance.cashOrChecks"
              required-radio>
              <ui-form-radio v-model="model.finance.cashOrChecks" :value="true" label="Yes" name="finance[cashOrChecks]" />
              <ui-form-radio v-model="model.finance.cashOrChecks" :value="false" label="No" name="finance[cashOrChecks]" />
            </ui-form-item>

            <br>
            <div class="uk-text-lead uk-heading-bullet">Petty Cash</div>

            <ui-form-item
              label="Will you require petty cash?"
              prop="finance.pettyCash.required"
              required-radio>
              <ui-form-radio v-model="model.finance.pettyCash.required" :value="true" label="Yes" name="finance[pettyCash][required]" />
              <ui-form-radio v-model="model.finance.pettyCash.required" :value="false" label="No" name="finance[pettyCash][required]" />
            </ui-form-item>

            <template v-if="model.finance.pettyCash.required">
              <ui-form-item label="What is the total amount of petty cash requested?">
                <ui-form-input v-model="model.finance.pettyCash.amount" name="finance[pettyCash][amount]" />
              </ui-form-item>

              <ui-form-item label="What are the specific denominations requested?">
                <ui-form-input v-model="model.finance.pettyCash.denominations" name="finance[pettyCash][denominations]" />
              </ui-form-item>

              <ui-form-item label="What date is the petty cash needed?">
                <ui-form-input v-model="model.finance.pettyCash.neededBy" name="finance[pettyCash][neededBy]" />
              </ui-form-item>
            </template>

            <br>
            <div class="uk-text-lead uk-heading-bullet">Gift Cards</div>

            <ui-form-item
              label="Are you planning to give gift cards?"
              prop="finance.giftCards.given"
              required-radio>
              <ui-form-radio v-model="model.finance.giftCards.given" :value="true" label="Yes" name="finance[giftCards][given]" />
              <ui-form-radio v-model="model.finance.giftCards.given" :value="false" label="No" name="finance[giftCards][given]" />
            </ui-form-item>

            <template v-if="model.finance.giftCards.given">
              <div class="uk-alert-primary" uk-alert>
                Gift cards will be purchased by the Finance Department. A list of recipient names is required
                before the gift cards are purchased. Employees/Contractors/Vendors of Zion Church are not
                eligible to receive gifts (monetary or non-monetary).
              </div>

              <div class="uk-child-width-1-3@m uk-margin" uk-grid>
                <div>
                  <ui-form-item label="What is the purpose of the gift cards?" required>
                    <ui-form-input v-model="model.finance.giftCards.purpose" name="finance[giftCards][purpose]" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item label="How many gift cards are requested?" required>
                    <ui-form-input v-model="model.finance.giftCards.qty" name="finance[giftCards][qty]" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item label="What is the denomination of each gift card?" required>
                    <ui-form-input v-model="model.finance.giftCards.denomination" name="finance[giftCards][denomination]" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item
                    label="Do you need physical or electronic gift cards?"
                    prop="finance.giftCards.physicalOrElectronic"
                    required-radio>
                    <ui-form-radio v-model="model.finance.giftCards.physicalOrElectronic" label="Physical" name="finance[giftCards][physicalOrElectronic]" />
                    <ui-form-radio v-model="model.finance.giftCards.physicalOrElectronic" label="Electronic" name="finance[giftCards][physicalOrElectronic]" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item label="What type of gift cards are requested?">
                    <ui-form-input v-model="model.finance.giftCards.type" name="finance[giftCards][type]" />
                    <template #help>
                      Amazon, store card, Visa, gas, etc.
                    </template>
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item label="What date are the gift cards needed?">
                    <ui-form-input v-model="model.finance.giftCards.neededBy" name="finance[giftCards][neededBy]" />
                  </ui-form-item>
                </div>
              </div>
            </template>

            <br>
            <div class="uk-text-lead uk-heading-bullet">Prizes/Giveaways</div>

            <ui-form-item
              label="Are you planning to give prizes/giveaways?"
              prop="finance.prizesGiveaways.given"
              required-radio>
              <ui-form-radio v-model="model.finance.prizesGiveaways.given" :value="true" label="Yes" name="finance[prizesGiveaways][given]" />
              <ui-form-radio v-model="model.finance.prizesGiveaways.given" :value="false" label="No" name="finance[prizesGiveaways][given]" />
            </ui-form-item>

            <template v-if="model.finance.prizesGiveaways.given">
              <div class="uk-alert-primary" uk-alert>
                Employees/Contractors/Vendors of Zion Church are not eligible to receive gifts
                (monetary or non-monetary).
              </div>

              <div class="uk-child-width-1-2@m" uk-grid>
                <div>
                  <ui-form-item label="What are the specific items to be given away?" required>
                    <ui-form-input v-model="model.finance.prizesGiveaways.items" name="finance[prizesGiveaways][items]" type="textarea" rows="4" />
                  </ui-form-item>
                </div>
                <div>
                  <ui-form-item label="What is the dollar value of each item?" required>
                    <ui-form-input v-model="model.finance.prizesGiveaways.values" name="finance[prizesGiveaways][values]" type="textarea" rows="4" />
                  </ui-form-item>
                </div>
              </div>

              <ui-form-item label="How many prizes/giveaways will be distributed?" required>
                <ui-form-input v-model="model.finance.prizesGiveaways.qty" name="finance[prizesGiveaways][qty]" />
              </ui-form-item>
            </template>
          </template>
        </ui-form-partial>

        <ui-form-partial label="Insurance">
          <ui-form-item
            label="Does your event require a special insurance certificate?"
            prop="insurance.requiresSpecialCertificate"
            required-radio>
            <ui-form-radio v-model="model.insurance.requiresSpecialCertificate" :value="true" label="Yes" name="insurance[requiresSpecialCertificate]" />
            <ui-form-radio v-model="model.insurance.requiresSpecialCertificate" :value="false" label="No" name="insurance[requiresSpecialCertificate]" />
          </ui-form-item>

          <div v-if="model.insurance.requiresSpecialCertificate" class="uk-alert-primary" uk-alert>
            <b>NOTE:</b> You MUST email the requirements to Finance before your event is approved.
          </div>
        </ui-form-partial>

        <button
          type="submit"
          class="uk-button uk-button-primary"
          @click.prevent="submit"
        >
          <span v-if="submitting" uk-spinner></span>
          <span v-else>Send</span>
        </button>
      </ui-form>
    </template>

    <p>&nbsp;</p>
    <p>&nbsp;</p>
    <p>&nbsp;</p>
    <p>&nbsp;</p>
    <p>&nbsp;</p>
    <p>&nbsp;</p>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert2'
import { v1 as uuid } from 'uuid'
import { serverUrl } from '../../common/helper/serverUrl'
import { getUrlParameter } from '../../common/helper/url'
import UiFormPartial from '../components/Form/FormPartial'

export default {
  components: { UiFormPartial },

  data() {
    return {
      initializing: false,
      done: false,

      error: null,

      model: {
        campus: [],
        name: '',
        poc: {
          name: '',
          phone: '',
          email: '',
        },
        department: '',
        pastorApproved: false,
        pastorName: '',
        ccb: {
          created: null,
          title: '',
          formLink: '',
        },
        event: {
          title: '',
          type: '',
          dates: [this.newEventDate()],
          registrationPeriod: '',
          location: '',
          crossPromoted: null,
          partnerArtwork: [],
          details: '',
        },
        textBlasts: {
          required: null,
          message: '',
        },
        graphics: {
          required: null,
          formats: [],
          specialFormatSize: '',
          detailsToInclude: '',
          designGuidelines: '',
          inspirationFiles: [],
        },
        designSupport: {
          required: null,
          services: [],
          additionalInformation: '',
        },
        registration: {
          required: null,
          ticketSalesOpen: {
            month: null,
            day: null,
            year: null,
          },
          ticketSalesClose: {
            month: null,
            day: null,
            year: null,
          },
          requirements: '',
        },
        video: {
          required: null,
          services: [],
        },
        finance: {
          monetaryTransactions: null,
          cashOrChecks: null,
          pettyCash: {
            required: null,
            amount: '',
            denominations: '',
            neededBy: '',
          },
          giftCards: {
            given: null,
            purpose: '',
            qty: '',
            denomination: '',
            physicalOrElectronic: null,
            type: '',
            neededBy: '',
          },
          prizesGiveaways: {
            given: null,
            items: '',
            values: '',
            qty: '',
          },
        },
        insurance: {
          requiresSpecialCertificate: null,
        },
      },

      submitting: false,

      campusOptions: [],
      graphicsFormatOptions: [
        'ProPresenter (On-screen announcement slide)',
        'Social Media (1080x1080, 1080x1920)',
        'Zion App',
        'Sermon Title',
        'Sermon Notes',
        'LED Wall',
        'Radio',
        'Flyers',
        'Special Format',
      ],
      designSupportOptions: [
        'Flyers',
        'Post cards',
        'Business cards',
        'Posters',
        'Signs, flags',
        'Merchandise For sale (t-shirts, tote bags, mugs, etc.)',
        'Branded Promo materials (giveaways) t-shirts, pens, hand sanitizers, masks, etc.',
      ],
      videoServiceOptions: [],
    }
  },

  computed: {
    validationRules() {
      const rules = {
        name: [],
        poc: {
          name: [],
          phone: [],
          email: [],
        },
      }

      return rules
    },

    initializeUrl() {
      return serverUrl('/promotion-form/initialize')
    },

    uploadParamsUrl() {
      return serverUrl('/promotion-form/file-upload-params')
    },

    submitUrl() {
      return serverUrl('/promotion-form/handle')
    },
  },

  methods: {
    async initialize() {
      this.initializing = true
      axios.get(this.initializeUrl)
        .then((resp) => {
          this.campusOptions = resp.data.campus
          this.videoServiceOptions = resp.data.videoService
          this.initializing = false
        })
        .catch(() => {
          swal('Error', 'Error initializing. Please refresh the page.', 'error')
        })
    },

    $_validation_validate() {
      return new Promise((resolve, reject) => {
        if (!('form' in this.$refs)) {
          resolve()
          return
        }

        this.$refs.form.validate()
          .then(() => {
            resolve()
          })
          .catch(() => {
            reject()
          })
      })
    },

    async submit() {
      this.$_validation_validate()
        .then(() => {
          this.submitting = true
          this.error = null

          const showError = (message) => {
            this.error = message
            window.scrollTo(0, 0)
          }

          axios
            .post(this.submitUrl, {
              data: this.model,
            })
            .then(_ => {
              this.done = true
              this.error = null
            })
            .catch(error => {
              if ('detail' in error.response.data) {
                showError(error.response.data.detail)
                Promise.resolve(error)
              }
            })
            .then(() => {
              this.submitting = false
            })
        })
        .catch(() => {
          this.$refs.form.focusOnFirstError()
        })
    },

    newEventDate() {
      return {
        id: uuid(),
        month: null,
        day: null,
        year: null,
      }
    },
  },

  created() {
    this.initialize()
    if (getUrlParameter('demo') === '1') {
      this.model = JSON.parse('{"campus":[11,10],"name":"John Doe","poc":{"name":"John Doe","phone":"555-555-5555","email":"duo@nickgreene.com"},"department":"Marketing","pastorApproved":true,"pastorName":"Charlie Smith","ccb":{"created":true,"title":"Fall Food Festival 2021","formLink":"example.com"},"textBlasts":{"required":true,"message":"Test 123"},"event":{"title":"Fall Food Festival 2021","type":"Event","dates":[{"id":"9fc71120-2aea-11ec-9a86-831ec2aa67f4","month":"11","day":"20","year":"2021"},{"id":"a96c1770-2aea-11ec-9a86-831ec2aa67f4","month":"11","day":"21","year":"2021"}],"registrationPeriod":"2021-10-01 - 2021-11-19","location":"Downtown Lenoir","crossPromoted":true,"partnerArtwork":["2021101173557--bananas.jpg"],"details":"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse vel lacinia sem. Donec ullamcorper malesuada nibh at efficitur. Sed euismod elit lectus, in vulputate mauris efficitur ac. Aenean dictum, nulla at imperdiet iaculis, orci arcu tincidunt arcu, sed finibus est nulla ut nisl. In aliquam turpis non quam varius, at consectetur dui viverra. Nulla facilisi. Nam eros mauris, congue quis gravida at, convallis sit amet tortor. Ut quis sagittis dui. Praesent lobortis hendrerit sem ac tristique. Integer pharetra semper dolor id pretium. Praesent suscipit orci eu consequat posuere. Suspendisse at est a tellus pellentesque volutpat. In in mollis magna. Maecenas molestie at eros et mattis.\\n"},"graphics":{"required":true,"formats":["Social Media (1080x1080, 1080x1920)","Flyers"],"specialFormatSize":"","detailsToInclude":"Date, time, vendors, COVID guidelines","designGuidelines":"","inspirationFiles":["2021101173512--cheeseburger.jpg","2021101173512--hotdogs.jpg"]},"designSupport":{"required":true,"services":["Flyers","Signs, flags"],"additionalInformation":"Test test\\nTest 123"},"registration":{"required":true,"ticketSalesOpen":{"month":"10","day":"11","year":"2021"},"ticketSalesClose":{"month":"11","day":"15","year":"2021"},"requirements":""},"video":{"required":true,"services":[1,2]},"finance":{"monetaryTransactions":true,"cashOrChecks":true,"pettyCash":{"required":true,"amount":"$20","denominations":"$5 or $10 or $20","neededBy":"Apr 1, 2022"},"giftCards":{"given":true,"purpose":"Incentive","qty":"20","denomination":"$20","physicalOrElectronic":"Physical","type":"VISA","neededBy":"Mar 1, 2022"},"prizesGiveaways":{"given":true,"items":"Gift baskets","values":"$60","qty":"4"}},"insurance":{"requiresSpecialCertificate":false}}')
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.campus) {
        this.$refs.campus.focus()
      }
    })
  },
}
</script>

<style lang="scss" src="../scss/app.scss">
</style>

<style lang="scss" src="../scss/page/PromoRequestForm.scss">
</style>
